import { useState, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Mic, MicOff, Check, AlertTriangle, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const confColors = {
  high: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  medium: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  low: 'bg-[#7a3535]/10 text-[#7a3535]'
};
const confLabels = { high: 'Yuksek', medium: 'Orta', low: 'Dusuk' };

export default function VoiceInput({ products, onConfirm }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [items, setItems] = useState(null);
  const [transcript, setTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      toast.error('Mikrofon erisim izni gerekli');
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) return;
    setRecording(false);
    setProcessing(true);

    const recorder = mediaRecorderRef.current;
    recorder.stop();
    recorder.stream.getTracks().forEach(t => t.stop());

    // Wait for data
    await new Promise(resolve => { recorder.onstop = resolve; });

    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', blob, 'recording.webm');

    try {
      const res = await api.post('/voice/parse-ingredients', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTranscript(res.data.transcript);
      setItems(res.data.items || []);
      if (!res.data.items?.length) {
        toast.error('Hicbir malzeme tespit edilemedi');
      }
    } catch (err) {
      toast.error('Ses isleme hatasi: ' + (err.response?.data?.detail || err.message));
    }
    setProcessing(false);
  }, []);

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'matched_product_id') {
      const p = products.find(x => x.id === value);
      if (p) {
        updated[idx].matched_product_name = p.name;
        updated[idx].matched_product_code = p.code;
        updated[idx].unit = p.unit;
        updated[idx].confidence = 'high';
      }
    }
    setItems(updated);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const handleConfirm = () => {
    const valid = items.filter(i => i.matched_product_id && i.quantity > 0);
    if (!valid.length) { toast.error('Gecerli malzeme yok'); return; }
    onConfirm(valid);
    setItems(null);
    setTranscript('');
    toast.success(`${valid.length} malzeme eklendi`);
  };

  const hasLowConfidence = items?.some(i => i.confidence === 'low');

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm" data-testid="voice-input-card">
      <CardHeader className="border-b border-[#d0c4b0] pb-3">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <Mic size={18} className="text-[#3d6e4e]" />
          Sesli Giris
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 md:p-6">

        {/* Recording controls */}
        {!items && (
          <div className="text-center space-y-4">
            <p className="text-sm text-[#8a7e70]">
              Butona basili tutarak malzeme listesini soyeleyin
            </p>
            <Button
              onMouseDown={startRecording}
              onMouseUp={stopRecording}
              onTouchStart={startRecording}
              onTouchEnd={stopRecording}
              disabled={processing}
              className={`h-20 w-20 rounded-full mx-auto flex items-center justify-center transition-all ${
                recording
                  ? 'bg-[#7a3535] text-white scale-110 shadow-lg animate-pulse'
                  : processing
                    ? 'bg-[#8a7e70] text-white'
                    : 'bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] hover:scale-105'
              }`}
              data-testid="voice-record-btn"
            >
              {recording ? <MicOff size={28} /> : <Mic size={28} />}
            </Button>
            <p className="text-xs text-[#8a7e70]">
              {recording ? 'Dinleniyor... Birakinca durur' : processing ? 'Isleniyor...' : 'Basili tut ve konus'}
            </p>
            {recording && (
              <div className="flex items-center justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-1.5 bg-[#7a3535] rounded-full animate-pulse"
                    style={{ height: `${12 + Math.random() * 20}px`, animationDelay: `${i * 100}ms` }} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Results preview */}
        {items && (
          <div className="space-y-4">
            {transcript && (
              <div className="p-3 bg-[#e8dfd0]/50 rounded-lg">
                <p className="text-xs font-bold uppercase text-[#8a7e70] mb-1">Algilanan Metin</p>
                <p className="text-sm text-[#1a1916] italic">"{transcript}"</p>
              </div>
            )}

            {hasLowConfidence && (
              <div className="flex items-center gap-2 p-2 bg-[#7a3535]/8 border border-[#7a3535]/20 rounded-md">
                <AlertTriangle size={16} className="text-[#7a3535] flex-shrink-0" />
                <p className="text-xs text-[#7a3535] font-medium">Bazi satirlar dusuk guvenle eslesti. Kontrol edin.</p>
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-24">Miktar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-20">Birim</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-20">Guven</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, i) => (
                  <TableRow key={i} className={`border-[#d0c4b0] ${item.confidence === 'low' ? 'bg-[#7a3535]/5' : ''}`}>
                    <TableCell>
                      <Select value={item.matched_product_id || ''} onValueChange={(v) => updateItem(i, 'matched_product_id', v)}>
                        <SelectTrigger className="bg-white border-[#d0c4b0] text-sm" data-testid={`voice-product-${i}`}>
                          <SelectValue placeholder={item.original_text || 'Urun sec'} />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" value={item.quantity || ''}
                        onChange={(e) => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                        className="bg-white border-[#d0c4b0] text-sm" data-testid={`voice-qty-${i}`} />
                    </TableCell>
                    <TableCell className="text-sm text-[#8a7e70]">{item.unit || '-'}</TableCell>
                    <TableCell>
                      <span className={`status-badge text-xs ${confColors[item.confidence] || ''}`}>
                        {confLabels[item.confidence] || item.confidence}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => removeItem(i)}
                        className="h-8 w-8 text-[#8a7e70] hover:text-[#7a3535]" data-testid={`voice-remove-${i}`}>
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setItems(null); setTranscript(''); }}
                className="border-[#d0c4b0] text-[#3F3E39]" data-testid="voice-cancel-btn">
                Iptal
              </Button>
              <Button onClick={handleConfirm}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold" data-testid="voice-confirm-btn">
                <Check size={16} className="mr-2" /> Listeyi Onayla
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
